// src/components/MilestoneCelebration.js
//
// The moment a badge is earned. Fires once, on the first Home visit after the
// workout that earned it — never for a backdated badge (claimNewlyEarned()
// silently seeds a member's existing badges on their first run).
//
// Phase 1 has no Milestone Wall yet, so the primary action is simply to
// acknowledge. The "share" action lands in phase 3.

import React, { useEffect, useState } from 'react';
import MilestoneBadge from './MilestoneBadge';

// Copy is deliberately specific per badge — "you did a thing" generic praise
// is exactly what makes this kind of feature feel automated.
const LINES = {
    first: 'You started. That\'s the part most people never do.',
    ten: 'Ten classes. This is starting to look like a habit.',
    tfive: 'Twenty-five classes. Not a burst of motivation — a routine.',
    fifty: 'Fifty classes. You\'ve built something that sticks.',
    hundred: 'One hundred classes. Take a second with that number.',
    quarter: 'Two hundred and fifty. Very few people ever get here.',
    mo1: 'Four weeks in a row. Consistency beats intensity every time.',
    mo3: 'Twelve weeks without missing one. That\'s who you are now.',
    mo6: 'Twenty-six weeks. Half a year of showing up for yourself.',
    gift26: 'Eighteen classes this holiday season. The best gift you gave this year was to yourself.',
};

function MilestoneCelebration({ badges, onClose, copy = {} }) {
    const [idx, setIdx] = useState(0);
    const [visible, setVisible] = useState(false);

    // Mount transparent, then fade in — avoids a hard pop on slower devices.
    useEffect(() => {
        const t = setTimeout(() => setVisible(true), 40);
        return () => clearTimeout(t);
    }, []);

    // Escape closes, alongside the button and the backdrop. Three ways out:
    // the button is the discoverable one (and the only accessible one), the
    // others are conventions power users expect.
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    if (!badges || badges.length === 0) return null;
    const badge = badges[idx];
    if (!badge) return null;

    const isLast = idx >= badges.length - 1;

    const advance = () => {
        if (isLast) onClose();
        else { setVisible(false); setTimeout(() => { setIdx(idx + 1); setVisible(true); }, 180); }
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 1000,
                background: 'var(--mom-overlay, rgba(45,41,38,.6))',
                backdropFilter: 'blur(6px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 20, animation: 'fadeIn 0.25s ease both',
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    // Explicit white — NOT --mom-card-bg, which is the admin's
                    // "Content Card" tile colour.
                    background: 'var(--mom-white, #fff)', borderRadius: 28,
                    padding: '40px 32px 28px', maxWidth: 380, width: '100%',
                    textAlign: 'center', position: 'relative', overflow: 'hidden',
                    boxShadow: '0 24px 70px rgba(45,41,38,.25)',
                    animation: 'modalIn 0.4s cubic-bezier(0.16,1,0.3,1) both',
                }}
            >
                {/* Warm bloom behind the badge — the only "effect" here. No confetti:
                    this brand celebrates quietly. */}
                <div style={{
                    position: 'absolute', top: -80, left: '50%', transform: 'translateX(-50%)',
                    width: 280, height: 210, pointerEvents: 'none',
                    background: 'radial-gradient(circle, color-mix(in srgb, var(--mom-primary) 18%, transparent), transparent 68%)',
                }} />

                <div style={{
                    position: 'relative',
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'scale(1)' : 'scale(0.88)',
                    transition: 'opacity 0.35s ease, transform 0.45s cubic-bezier(0.16,1,0.3,1)',
                    display: 'flex', justifyContent: 'center',
                }}>
                    <MilestoneBadge badgeKey={badge.key} size={132} />
                </div>

                <p style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 10.5, fontWeight: 700,
                    letterSpacing: '0.15em', textTransform: 'uppercase',
                    color: 'var(--mom-warm-gray)', marginTop: 22, position: 'relative',
                }}>
                    {copy.milestoneCelebrationEyebrow || 'Milestone reached'}
                </p>

                <h3 style={{
                    fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 26,
                    fontWeight: 600, color: 'var(--mom-charcoal)', margin: '6px 0 8px', position: 'relative',
                }}>
                    {badge.name}
                </h3>

                <p style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 14, lineHeight: 1.65,
                    color: 'var(--mom-warm-gray)', position: 'relative', margin: 0,
                }}>
                    {/* Per-badge line is admin-overridable (milestoneCelebrationLine_<key>);
                        only the seasonal badge exposes a field today. */}
                    {(typeof copy[`milestoneCelebrationLine_${badge.key}`] === 'string' && copy[`milestoneCelebrationLine_${badge.key}`].trim())
                        || LINES[badge.key] || badge.requirement}
                </p>

                <button
                    onClick={advance}
                    className="mom-btn mom-btn-primary"
                    style={{
                        marginTop: 26, width: '100%', padding: 14, borderRadius: 14,
                        fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 700,
                        position: 'relative',
                    }}
                >
                    {isLast
                        ? (copy.milestoneCelebrationCloseLabel || 'Keep moving')
                        : `Next (${idx + 1} of ${badges.length})`}
                </button>
            </div>
        </div>
    );
}

export default MilestoneCelebration;
